import { memo, useMemo } from "react";
import { NOTE_ORDER } from "@/music/chords/chordParser";
import { getBassTriadDetails } from "@/music/chords/chordTheoryEngine";

const STRINGS = [
  { name: "G", note: "G" },
  { name: "D", note: "D" },
  { name: "A", note: "A" },
  { name: "E", note: "E" },
];
const DOT_COLORS = ["#22d3ee", "#a78bfa", "#34d399"];
const fretFor = (open, target) => (NOTE_ORDER.indexOf(target) - NOTE_ORDER.indexOf(open) + 12) % 12;

const choosePositions = (notes) =>
  notes.map((note, index) => {
    const preferredString = index === 0 ? 2 : index === 1 ? 1 : 0;
    const candidates = STRINGS.map((string, stringIndex) => ({
      stringIndex,
      fret: fretFor(string.note, note),
    }));
    candidates.sort((a, b) => Math.abs(a.stringIndex - preferredString) - Math.abs(b.stringIndex - preferredString) || a.fret - b.fret);
    return { note, index, ...candidates[0] };
  });

const BassTriadDiagram = memo(({ chordName, root, quality, compact = false, rootOnly = false, variant = "dark" }) => {
  const parsedDetails = useMemo(() => getBassTriadDetails(chordName || `${root || ""}${quality || ""}`), [chordName, quality, root]);
  const details = rootOnly && parsedDetails.root
    ? {
        ...parsedDetails,
        triad: [parsedDetails.root],
        displayTriad: [parsedDetails.displayRoot],
        educational: false,
        intervalLabels: ["R"],
      }
    : parsedDetails;
  const positions = useMemo(() => choosePositions(details.triad), [details.triad]);

  return (
    <figure className="w-full" aria-label={`Triada de bajo ${details.displayName || "sin acorde"}`}>
      <div className={`mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs ${variant === "light" ? "text-slate-500" : "text-cyan-100/75"}`}>
        <span>{details.educational ? `Triada de bajo: ${details.displayTriad.join(" - ")}` : `Raiz detectada: ${details.displayRoot || "--"}`}</span>
        {!details.educational && details.recognized && <span className={variant === "light" ? "text-amber-700" : "text-amber-300"}>Solo raiz fiable</span>}
      </div>
      <div className="steel-scroll-soft overflow-x-auto">
        <svg viewBox="0 0 400 124" className={`min-w-[20rem] w-full ${compact ? "h-[6.7rem]" : "h-[7.75rem]"}`} role="img">
          <rect x="22" y="14" width="366" height="92" rx="7" fill="#0f172a" stroke="#334155" />
          {Array.from({ length: 13 }).map((_, fret) => (
            <g key={`fret-${fret}`}>
              <line x1={48 + fret * 26} x2={48 + fret * 26} y1="30" y2="94" stroke={fret === 0 ? "#bae6fd" : "#334155"} strokeWidth={fret === 0 ? 2 : 1} />
              <text x={48 + fret * 26} y="25" textAnchor="middle" fill="#64748b" fontSize="8">{fret}</text>
            </g>
          ))}
          {STRINGS.map((string, index) => (
            <g key={string.name}>
              <text x="34" y={43 + index * 17} textAnchor="middle" fill="#cbd5e1" fontSize="10" fontWeight="700">{string.name}</text>
              <line x1="48" x2="360" y1={40 + index * 17} y2={40 + index * 17} stroke="#94a3b8" strokeWidth={1.1 + index * 0.2} />
            </g>
          ))}
          {positions.map((position) => (
            <g key={`${position.note}-${position.index}`}>
              <circle cx={48 + position.fret * 26} cy={40 + position.stringIndex * 17} r="9" fill={DOT_COLORS[position.index]} />
              <text x={48 + position.fret * 26} y={43 + position.stringIndex * 17} textAnchor="middle" fill="#07111e" fontSize="8" fontWeight="800">
                {details.intervalLabels[position.index]}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <figcaption className={`text-center text-sm font-semibold ${variant === "light" ? "text-slate-800" : "text-cyan-100"}`}>{details.displayName || "--"}</figcaption>
    </figure>
  );
});

BassTriadDiagram.displayName = "BassTriadDiagram";

export default BassTriadDiagram;
